import { useState, type InputHTMLAttributes } from "react";

/**
 * A whole number that is saved when the person is done typing (blur or Enter), not on every keystroke: clearing
 * the field to type "25" must not save 1 first, and "10" must be reachable when 1 is below the minimum (bug hunt
 * D6). A value outside `min`–`max`, or none, puts the saved value back; so does Escape.
 */
export function NumberInput({
  value,
  min,
  max,
  onCommit,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "min" | "max" | "type"> & {
  value: number;
  min: number;
  max: number;
  onCommit: (n: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const n = Number(draft);
    setDraft(null);
    if (draft.trim() !== "" && Number.isInteger(n) && n >= min && n <= max && n !== value) onCommit(n);
  };
  return (
    <input
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      {...props}
      value={draft ?? String(value)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        else if (e.key === "Escape") setDraft(null);
      }}
    />
  );
}
